import { useCallback, useState } from 'react';

import dayjs from 'dayjs';
import {
  GET_SWING_CONTRACT_MAPPING_DATA_BY_ID,
  SWING_CONTRACT_MESSAGES,
} from '../../../constants/FieldNomsSwingContract';
import { HTTPS_STATUS_CODES } from '../../../constants/Common';
import { get } from '../../../services/GenesisApi';
import { SwingContractMapping } from '../../../types/FieldNomsSwingContract.type';
import { sendNotification } from '../../../UI/Notification';

const { FAILED_FETCH_CONTRACT_MAPPING_DATA } = SWING_CONTRACT_MESSAGES;

const useSwingContractEdit = ({ reset }) => {
  const [mappingData, setMappingData] = useState<SwingContractMapping | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAndPopulateData = useCallback(
    async (
      selectedItem: SwingContractMapping
    ): Promise<SwingContractMapping | undefined> => {
      if (!selectedItem?.id) {
        return undefined;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await get(
          `${GET_SWING_CONTRACT_MAPPING_DATA_BY_ID}?id=${selectedItem.id}`
        );

        if (response.status === HTTPS_STATUS_CODES.SUCCESS) {
          const { data } = response;
          setMappingData(data);

          const { validFrom, validTo } = data;

          const zKeyValue = data.zKey ?? data.zkey ?? '';

          reset({
            deal_DealLegId: data.deal_DealLegId ?? '',
            dealUnit: data.dealUnit ?? 'Therms',
            zKey: zKeyValue,
            contractNameId: data.contractNameId,
            entryOrExitId: data.entryOrExitId,
            description: data.description,
            locationOrExitMeter: data.locationOrExitMeter,
            geminiActivityNumber: data.geminiActivityNumber,
            unit: data.unit,
            validFrom: validFrom ? dayjs(validFrom) : undefined,
            validTo: validTo ? dayjs(validTo) : undefined,
            source: data.source,
            destination: data.destination ?? '',
          });
          return data;
        }

        throw new Error(
          response.statusText ?? FAILED_FETCH_CONTRACT_MAPPING_DATA
        );
      } catch (err) {
        setError((err as string) ?? FAILED_FETCH_CONTRACT_MAPPING_DATA);
        sendNotification({
          type: 'error',
          message: (err as string) ?? FAILED_FETCH_CONTRACT_MAPPING_DATA,
        });
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [reset]
  );

  return { mappingData, loading, error, fetchAndPopulateData };
};

export default useSwingContractEdit;
